import React from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { NAVIGATION_STYLES } from '../../../config/constants';
import { useCustomerLogo } from '../../../hooks/useCustomerLogo';

/**
 * NavigationHeader Component
 *
 * Displays the logo, tenant information, and toggle button for the navigation drawer.
 * Used in the expanded state of the navigation.
 *
 * @param {Object} props - Component props
 * @param {Object} props.tenant - Current tenant information
 * @param {Function} props.onToggle - Callback function for toggling navigation
 */
const NavigationHeader = ({ tenant, onToggle }) => {
  const theme = useTheme();
  const { logoUrl, handleImageError } = useCustomerLogo();

  return (
    <Box sx={{ p: 1.5, pt: 1.5 }}>
      {/* Logo and Toggle Button Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1.5,
          position: 'relative',
        }}
      >
        <Box
          component="img"
          src={logoUrl}
          alt="Logo"
          sx={{
            height: NAVIGATION_STYLES.logo.expanded.height,
            width: NAVIGATION_STYLES.logo.expanded.width,
            objectFit: 'contain',
            mx: 'auto',
          }}
          onError={handleImageError}
        />
        <IconButton
          onClick={onToggle}
          sx={{
            position: 'absolute',
            right: 0,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.divider,
              color: theme.palette.text.primary,
            },
          }}
        >
          <ChevronLeft />
        </IconButton>
      </Box>

      {/* Tenant Information */}
      {tenant && (
        <Box
          sx={{
            p: 0.75,
            px: 1.25,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.primary.dark
                : theme.palette.primary.light,
            borderRadius: '999px',
            border: `1px solid ${theme.palette.primary.main}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.primary.contrastText,
              fontSize: '0.8rem',
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            Tenant:{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
              }}
            >
              {tenant.alias || tenant.name}
            </Box>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NavigationHeader;
